import { Dispatch, SetStateAction, useState } from 'react';
import SharedDialog from '../../shared/shared-dialog';
import ChangeLogDialog from '../../shared/change-log-dialog';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { Policy } from '../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePolicies, UserKeys } from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { useStore } from '../state/zustand-store';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeletePoliciesDialogProps {
  open: boolean;
  handleClose: () => void;
  onDeleteSuccess?: () => void;
  changeLogRoles: string[];
  selectedPolicies: number[];
  setSelectedPolicies: Dispatch<SetStateAction<number[]>>;
  policyList: Policy[];
}

export default function DeletePoliciesDialog({
  open,
  handleClose,
  onDeleteSuccess,
  changeLogRoles,
  selectedPolicies,
  setSelectedPolicies,
  policyList,
}: DeletePoliciesDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const policies = policyList.filter((policy) =>
    selectedPolicies.includes(policy.id)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => deletePolicies(selectedPolicies, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedPolicies.length > 1
            ? 'Policies deleted successfully!'
            : 'Policy deleted successfully!',
      });
      queryClient.setQueryData<Policy[] | undefined>(
        [UserKeys.Policies],
        (oldData) => {
          if (!oldData) return;
          return oldData.filter(
            (policy) => !selectedPolicies.includes(policy.id)
          );
        }
      );
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      setSelectedPolicies([]);
      handleClose();
      onDeleteSuccess?.();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedPolicies.length > 1
            ? 'There was an issue deleting the selected policies. Please refresh and try again.'
            : 'There was an issue deleting this policy. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.policies.delete_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.policies,
      });
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DeletePolicy.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const handleDeleteWithChangeLog = () => {
    mutate();
  };

  const isMultiple = policies.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isMultiple ? 'Delete Policies' : 'Delete Policy'}
      dialogContent={
        <Box>
          <Typography>
            {isMultiple ? (
              <>
                Delete <strong>{policies.length} policies?</strong>
              </>
            ) : policies.length === 1 ? (
              <>
                Delete <strong>{policies[0].policyName}?</strong>
              </>
            ) : (
              'No policies selected to delete.'
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={handleDeleteWithChangeLog}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isMultiple ? 'Delete Policies' : 'Delete Policy',
        color: 'error',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
